import { type documentTypeModel } from '../Components/DocumentList'
import { type notificationEvent } from '../Pages/CareNetworkManagement/CareNetworkDetail/ChildHistoryDisplay'

export enum Role {
  CARE_GIVER = 'Caregiver',
  SERVICE_PROVIDER_USER = 'ServiceProviderUser',
  ISD_USER = 'ISDUser',
  MIECC_ADMIN = 'MiECCAdmin',
  INTAKE_USER = 'IntakeUser',
}
export enum DayOfWeek {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday
}
export enum NoteType {
  ContactLog = 'ContactLog',
  Reminder = 'Reminder',
  Note = 'Note'
}
export enum MonthOfYear {
  January,
  February,
  March,
  April,
  May,
  June,
  July,
  August,
  September,
  October,
  November,
  December
}
export enum IsdDocumentTypeStatus {
  AVAILABLE = 'Available',
  NOTAVAILABLE = 'Not Available',
  REQUIRED = 'Required',
  NOTREQUIRED = 'Not Required'
}
export enum CaregiverAccessType {
  PRIMARYCAREGIVER = 'Primary Caregiver',
  CAREGIVER = 'Caregiver',
  CUSTODIAL_GUARDIAN = 'Custodial Guardian'
}

export enum NeedsMetStatus {
  YES = 'Yes',
  NO = 'No',
  PREFERNOTTOSAY = 'PreferNotToSay',
}
export enum Status {
  INACTIVE = 'Inactive',
  IN_PROGRESS = 'InProgress',
  ACTIVE = 'Active'
}

export enum SecurityLevel {
  NONE = 'None',
  VIEW = 'ViewOnly',
  MODIFY = 'Edit'
}

export enum DocumentTarget {
  CHILD = 'Child',
  BOTH = 'Both',
  ADULT = 'Adult'
}

export enum FacilityStatus {
  INACTIVE = 'Inactive',
  ACTIVE = 'Active'
}

export interface ActiveAccount {
  id: number
  type: AccountType
  v4Enabled: boolean
  canCreateChild: canCreateChild
  currentISDID?: number | null
}

export enum canCreateChild {
  No = 0,
  Mixed = 1,
  Yes = 2
}
export enum AccountType {
  ISD = 'ISD',
  SERVICE_PROVIDER = 'Service Provider',
  INTAKE_USER = 'Intake'
}

export enum EnrollmentStatus {
  Pending = 'Pending',
  EnrollPendingApproval = 'EnrollPendingApproval',
  ProgramHoldPendingApproval = 'ProgramHoldPendingApproval',
  Waitlist = 'Waitlist',
  Enrolled = 'Enrolled',
  Exited = 'Exited',
  ApprovalDenied = 'ApprovalDenied'
}
export enum NewEnrollmentStatus {
  PendingRelease = 0,
  Referred = 1,
  Processing = 2,
  Declined = 3,
  Waitlisted = 4,
  Enrolled = 5,
  Started = 6,
  Exited = 7,
}

export enum CommunicationType {
  Communication = 'Communication',
  Reminder = 'Reminder',
  Note = 'Note'
}

export enum WaiverDenyOptions {
  ReferToMe = 'ReferToMe',
  ReturnToIntake = 'ReturnToIntake'
}

export enum WaiverAcceptOptions {
  ReferToMe = 'ReferToMe',
  ReturnToIntake = 'ReturnToIntake'
}

export enum ReferralTimeFrame {
  Immediate = 'Immediate',
  UpcomingSchoolYear = 'UpcomingSchoolYear',
  SpecificDate = 'SpecificDate'
}

export enum ReferralTimeFrameDisplay {
  Immediate = 'Immediate',
  UpcomingSchoolYear = 'Upcoming School Year',
  SpecificDate = 'Specific Date'
}

export enum DesiredSchedule {
  Morning = 'Morning',
  Afternoon = 'Afternoon',
  Evening = 'Evening',
  AllDay = 'AllDay'
}

export enum SearchType {
  Filter = 'Filter',
  Map = 'Map',
  ReachOut = 'ReachOut'
}

export enum ChildAuditAccessTypeEnum {
  Referral,
  Enrollment,
  CareNetwork
}

export enum NoteSource {
  ContactLog = 'ContactLog',
  Note = 'Note',
  Reminder = 'Reminder',
  ReferralReminder = 'ReferralReminder',
  EnrollmentReminder = 'EnrollmentReminder',
  ReferralNote = 'ReferralNote',
  WaiverNote = 'WaiverNote',
  EnrollmentNote = 'EnrollmentNote',
  AccessRequestNote = 'AccessRequestNote',
  NoteOnReferral = 'NoteOnReferral',
  ReferralDenial = 'ReferralDenial',
  VerificationNote = 'VerificationNote',
  IncomeApprovalRequest = 'IncomeApprovalRequest',
  IncomeApprovalResponse = 'IncomeApprovalResponse',
  EnrollmentDeclineNote = 'EnrollmentDeclineNote',
}

export type UserFunction =
  | 'CaregiverPortal'
  | 'CaregiverPortal_Write'
  | 'CaregiverPortal_Enrollments'
  | 'CaregiverPortal_Enrollments_Write'
  | 'CaregiverPortal_Details'
  | 'CaregiverPortal_Details_Write'
  | 'CareNetwork'
  | 'CareNetwork_Caregiver'
  | 'CareNetwork_Caregiver_Edit'
  | 'CareNetwork_NetworkMembers'
  | 'CareNetwork_NetworkMembers_Edit'
  | 'CareNetwork_Documentation'
  | 'CareNetwork_Documentation_Edit'
  | 'CareNetwork_IncomeRequest'
  | 'CareNetwork_IncomeRequest_Edit'
  | 'CareNetwork_SensitiveDetails'
  | 'CareNetwork_SensitiveDetails_Edit'
  | 'CareNetwork_MSDS'
  | 'CareNetwork_MSDS_Edit'
  | 'CareNetwork_Enrollment'
  | 'CareNetwork_Enrollment_Edit'
  | 'CareNetwork_Communication'
  | 'CareNetwork_Communication_Edit'
  | 'Enrollment'
  | 'Enrollment_Edit'
  | 'Reports'
  | 'Users'
  | 'Users_Edit'
  | 'Impersonation'
  | 'ServiceProviders'
  | 'ServiceProviders_Edit'
  | 'ISDs'
  | 'ISDs_Edit'
  | 'ISDs_Approvals'
  | 'ISDs_Approvals_Edit'
  | 'MiECCAdmin'
  | 'Enrollment_Documentation'
  | 'Enrollment_Communication'
  | 'Enrollment_Documentation_Edit'
  | 'Enrollment_Communication_Edit'
  | 'CareNetwork_MergeRecords'
  | 'WaiverApprovals'
  | 'CareNetwork_SupplementalNutrition'
  | 'CareNetwork_SupplementalNutrition_Edit'
  | 'CareNetwork_CareManagement'

export interface RoleInfo {
  id: Role
  manageableRoles: RoleInfo[]
  implicitFunctions: UserFunction[]
  explicitFunctions: UserFunction[]
}

export interface ApproveWaiverRequest {
  id: number
  approvalReasonId?: number | null
}

export interface DenyWaiverRequest {
  id: number
  notes: string
  denyReasonId?: number | null
  waiverOption?: WaiverDenyOptions | null
}

export interface DenyReferralRequest {
  referralId: number
  note: string
  serviceProviderId?: number | null
  denyReasonId?: number | null
  manualReferEnabled?: boolean | null
}

export interface AcceptReferralRequest {
  referralId: number
  sessionBlockId?: number | null
  facilityId?: number | null
  sessionId?: number | null
  enrollNow: boolean
  enrollmentDate?: Date | null
  startDate?: Date | null
  priorityProviderId?: number | null
  priorityNotes?: string | null
  requiresPriority?: boolean | null
}

export interface CareNetworkCommunication {
  id: number
  enteredOn: Date
  note: string
  needMet: NeedsMetStatus | null
  addressedWhatMatteredMost: NeedsMetStatus | null
  reasonNotMet: string | null
  type: NoteType
  reminderDate: Date | null
  userID: number | null
  caregiverID: number | null
  contactMethodID: number | null
  childID: number | null
  referralID: number | null
  enrollmentID: number | null
}

export interface AuthorizedFunction { id: number, authorizedFunction: UserFunction }

export interface User {
  id: number
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  inactive: boolean
  externalLogin: boolean
  isImpersonating: boolean
  isFakeAdmin: boolean
  securityLevel: Role
  userAuthorizedFunctions: AuthorizedFunction[]
  isDs: ISD[]
  currentISDID: number | null
  selectedServiceProviderID: number | null
  serviceProviders: isdServiceProvider[]
  noAccount: boolean
  imageKey?: string
  userNotificationSettings: userNotificationSetting[]
  notifications: notificationEvent[]
  noActiveISDs: boolean
}

export interface caregiver {
  id: number
  firstName: string
  lastName: string
  primaryPhone: string
  secondaryPhone: string
  email: string
  preferredContactMethod: number
  educationLevelID: number
  userID: number
}

export interface contactmethod {
  id: number
  name: string
}
export interface lookup {
  id: number
  name: string
}
export interface BasicEnrollmentData {
  id: number
  isdID: number
  childID: number
  childName: string
  programVerificationID: number | null
  serviceName: string
  isdSpServiceID: number
  facilityName: string
  serviceID: number
  programID: number | null
  timeSpanID: number | null
  reportURL: string
}
export interface CaseloadHistoryObject {
  id: number
  orgName: string
  startDate: Date
  endDate: Date
  caregiverNames: string[]
  careSummary: string
  reasonId: number | null
  reason: string
  otherReason: string | null
  isdName: string
  providedEducations: string[]
  serviceLogs: ServiceLog[]
  needsMet: boolean
  addressedMostImportant: boolean
  reasonNotMet: string
  connectedToReferral: boolean
}

export interface PersonBasicInfo {
  id: number
  firstName: string
  lastName: string
  suffix: string
  isdId?: number
  districtId?: number
  caseloadinfo: CaseloadInfo | null
}
export interface CaseloadInfo {
  orgId?: number
  userId?: number
  userName?: string
  userPhone?: string
  userEmail?: string
  orgEmail?: string
  orgPhone?: string
  removalReasonId?: number
  removalReasonName?: string
  orgName?: string
  orgWebsite?: string
  isdName?: string
  users?: string[]
  providedEducations: string[]
  serviceLogs: ServiceLog[]
  availableIntakes: AvailableIntake[]
}
export interface AvailableIntake {
  id: number
  name: string
  s3Key: string | null
}
export interface caregiverChildRelationship {
  id: number
  caregiverID: number
  childID: number
  relationshipID: number
  otherRelationship: string
  legalGuardian: boolean
  careGiver?: ChildCaregiver
}

export interface enrollment {
  id: number
  child: child
  program: program
  programID: number
  status: string
  incomeApprovalStatus?: string
}

export interface ChildCaregiver {
  id: number
  firstName: string
  lastName: string
  relationshipName: string
  accessType: string
  isLivingWithStudent: boolean
  email: string
  dateOfBirth: Date | null
  languageID: number | null
  educationLevelID: number | null
  relationshipID: number | null
  preferredContactMethod: number | null
  isFinancialResponsible: boolean
  createAccount: boolean | null
  childId: number
  securityLevel: SecurityLevel
  primaryPhone: string | null
  secondaryPhone: string | null
  address: string | null
  city: string | null
  state: string | null
  apartment: string | null
  zipCode: string | null
  childSiblings: ChildSiblings[]
  newChildSiblings: number[]
  incomes?: income[]
  documents?: documentTypeModel[]
}

export type ChildSiblings = ChildEditValues & relationshipData

interface relationshipData {
  relationshipName: string
  isLivingWithStudent: boolean
  isSiblingRecord: boolean
  siblingChildId: number
  shareAllCaregivers: boolean
  caregiverFirstName: string
  caregiverLastName: string
  relationshipID: number
  otherRelationship: string | null
  address: string
  apartment: string
  city: string
  state: string
  zipCode: string
}

export interface ChildEditValues {
  id: number
  firstName: string
  lastName: string
  middleName: string | null
  suffix: string | null
  nickName: string | null
  dateOfBirth: Date | null
  birthGender: string
  uic: string | null
  languageID: number | null
  image: string | null
  canEditEligibilityFactors: boolean
  childRaceList: race[]
  eligibilityFactorList: eligibilityFactor[]
  environmentalRiskList: environmentalRisk[]
  otherEligibilityFactor: string | null
  ageDisplay: string | null
  language: string | null
  primaryLocationID: number | null
  residentDistrict: string | null
  childLocations: location[]
  documentTypeRequirements: DocumentTypeRequirement[]
  childRelationships?: caregiverChildRelationship[]
  tribalAffiliationStatus?: boolean
  tribalAffiliationID?: number | null
  tribalAffiliationName?: number | null
  captureIEP: boolean
  captureIFSP: boolean
  captureEOIS: boolean
  hasIEP: boolean
  hasIFSP: boolean
  hasEOIS: boolean
  isdID?: number
  hasSNAPBenefits: boolean
  isFosterChild: boolean
  isOnCashAssistance: boolean
  isOnSSI: boolean
  isInformationalOnly: boolean
  authorizationStatusID: number | null
  authorizationStatus: string | null
  isUnborn: boolean
  dueDate: Date | null
  isImmigrant: boolean
  iepRecommendsInclusivePlacement: boolean
  imageFile?: string | null
  canViewEligibilityFactors?: boolean
  caregivers?: ChildCaregiver[]
  childSiblings?: ChildSiblings[]
  endDate: Date | null
  reasonEndingID: number | null
  reasonEndingComment: string | null
  exitReasonID: number | null
  interestFormDetails: InterestFormDetails | null
}
export interface InterestFormDetails {
  notes: string
  interestedServices: InterestedService[]
  visible: boolean
}
export interface InterestedService {
  name: string
  detail: string | null
  s3Key: string | null
}
export const OtherReasonEndingID: number = 3 // this is the ID in the ReasonEnding table for "Other"

export interface DocumentTypeRequirement {
  documentTypeID: number
  required: boolean
  documentTarget: DocumentTarget
}

export interface child {
  id: number
  firstName: string
  lastName: string
  fullName: string
  middleName: string
  suffix: string
  nickName: string
  dateOfBirth: Date | null
  birthGender: string
  uic: string
  hasIEP: boolean
  fosterChild: boolean
  hasSNAPBenefits: boolean
  primaryLanguage: string
  yearsMonthsAge: string
  races?: string[]
  eligiblityFactors?: string[]
  povertyPercentage?: number
}

export interface AccessControlModel {
  caregiverId: number
  childId: number
  securityLevel: SecurityLevel
}

export interface district {
  id: number
  iSDID: number
  stateCode: string
  name: string
  nCES_Code: string
}

export interface document {
  id: number
  careNetworkID: number
  caregiverID: number
  childID: number
  fileTypeID: number
  fileName: string
  whenUploaded: Date
}

export interface educationLevel {
  id: number
  name: string
  stateCode: string
}

export interface eligibilityFactor {
  id: number
  name: string
  stateCode: string
}

export interface environmentalRisk {
  id: number
  name: string
}

export interface exportPeriod {
  id: number
  name: string
  code: string
  periodStart: string
  periodEnd: string
  collectionID: number
  majorVersion: string
  minorVersion: string
}

export interface facility {
  id: number
  serviceProviderID: number
  name: string
  facilityCode: string
  licenseNumber: string
  licenseExpirationDate: Date | null
  phoneNumber: string
  website: string
  inactive: boolean
  lastModified: Date
  facilityHours: businessHours[]
  location: location
  qualityRating: number | null
  qualityRatingName?: string | null
  isdid: number | null
  isdName?: string | null
  locationISDFound: boolean
  offeringNarrative: string | null
  services: lookup[]
  programs: lookup[]
  providesBeforeAfterSchoolCare: boolean
}

export interface businessHours {
  id: number
  facilityID: number
  dayOfWeek: string
  dayDisplay: string
  openingTime: string
  closingTime: string
}

export interface facilityRoom {
  id: number
  facilityID: number
  name: string
  capacity: number | null
  noCapacityLimit: boolean
  hasAttachedSessions: boolean
  federallyFunded: boolean
  inactive: boolean
  programs: ProviderProgram[]
}

export interface ProviderProgram {
  id: number
  name: string
  enabled: boolean
  noCapacityLimit: boolean
}

export interface federalPovertyLevel {
  id: number
  year: number
  baselineAmount: number | null
  increasePerPerson: number | null
}

export interface fileType {
  id: number
  name: string
  description?: string | null
  inactive: boolean
  documentTarget: string
  s3Key: string | null
  incomeRelated: boolean
}

export interface fundingSource {
  id: number
  name: string
}

export interface income {
  id: number
  careNetworkID: number
  year: number
  annualIncome: number
  memberCount: number
  unknownIncome: boolean
}

export interface userNotificationSetting {
  id: number
  userID: number
  eventType: string
  caregiverID: number
  isdid: number
  intakeServiceProviderID: number
  isdServiceProviderID: number
  serviceProviderID: number | null
  isAppNotificationEnabled: boolean
  appSubscriptionDate: Date | null
  isEmailNotificationEnabled: boolean
  emailSubscriptionDate: Date | null
}

export interface ISD {
  id: number
  name: string
  notes?: string
  intakeProviderID: number | null
  website: string
  address: string
  city: string
  state?: string
  zipCode: string
  contactEmail: string
  contactPhone: string
  logoKey?: string | null
  status: string
  onboardDate: Date
  top3Selection: boolean
  captureIFSP: boolean
  captureIEP: boolean
  captureEOIS: boolean
  showResultsFromOtherCounties: boolean
  selectProviderOnReferralDecline: boolean
  referAllToIntakeProvider: boolean
  adminUserId: number | null
  showServicesForMyChild: boolean
  showServicesForMyself: boolean
  showCreateReferral: boolean
  adultServicesName?: string
  adultServicesLink?: string
  caregiverPortalMaxProviders?: number
  v4Enabled: boolean | null
}

export interface isdFileType {
  id: number
  isdID: number
  fileTypeID: number
  programID: number
}

export interface isdProgram {
  id: number
  isdid: number
  programID: number
  name: string
  allowConcurrentEnrollment: boolean
  priorityProgram: boolean
  prioritizeOtherProgram: boolean
  prioritizedProgramID: number | null
  overIncomeCutOffFourPlus: number | null
  overIncomeCutOffThree: number | null
  overIncomeCutOffZeroToTwo: number | null
  enabled: boolean
  fundingSource?: fundingSource
  requiresIncomeCalculation: boolean
  requiresManualVerification: boolean
  isIntake: boolean
  noCapacityLimit: boolean
  hasEnrollments: boolean
  ageSettingList: ageSetting[]
  description: string
  autoWaiverCreateForEligibleChildren: boolean
  selfApproveOverIncome?: boolean
  incomeVerificiationModeID: number | null
  allowContinuousEnrollment: boolean

}
export interface fplSettings {
  lowAge: number
  highAge: number
  fplIncomeThreshhold: number | null
}

export interface ageSetting {
  id: number
  allowForAge: number
  fplIncomeThreshhold: number | null
  requiresApproval: boolean | null
  maxEnrollmentAllocationPercent: number | null
}

export interface location {
  id: number
  address: string
  apartment: string
  city: string
  state: string
  zipCode: string
  note: string
  isPrimary?: boolean
  districtID?: number
  districtName?: string
  isdName?: string
  livesWith?: string[]
  isHomeless?: boolean
  startDate?: Date | null
  endDate?: Date | null
  homelessResidenceID?: number | null
  homelessResidence?: string | null
  isUnaccompaniedYouth?: boolean | null
}

export interface program {
  id: number
  name: string
  stateCode: string
  inactive: boolean
  fundingSourceID: number
  description: string
  requiresIncomeCalculation: boolean
  programId?: number | null
}

export interface race {
  id: number
  name: string
  codePosition: number
}

export interface relationship {
  id: number
  name: string
}

export interface dateRange {
  id: number
  name: string
  startDate: Date | null
  endDate: Date | null
  years: number[]
}

export interface seatAllocation {
  id: number
  sessionID: number
  programID: number
  name?: string
  seats: number
}

export interface service {
  id: number
  isdid?: number
  name: string
  description: string
  s3Key: string | null
  inactive: boolean
  serviceId?: number | null
}

export interface serviceOffering {
  id: number
  serviceProviderID: number
  serviceID: number
  schoolYearID: number
}
export interface serviceProvider {
  id: number
  name: string
  eemCode: string
  onboardDate: Date
  status: string
  userID: number | null
  imageKey: string | null
  website: string
  contactEmail: string
  contactPhone: string
  location?: location
  careManagement: boolean
}

export interface isdServiceProvider {
  id: number
  isdid: number
  name: string
  eemCode: string
  onboardDate: Date
  status: string
  userID: number | null
  s3Key: string | null
  isIntake: boolean
  location?: location
  allowUICLookup: boolean
  anyCanCreateChild: boolean
  allCanCreateChild: boolean
  canCreateChild: boolean
  sftpFile: boolean
  serviceList: ISDServiceOption[]
  programList: ISDProgramOption[]
  serviceProviderID?: number
  isdServiceProviderID?: number
  v4Enabled?: boolean
}

export interface ISDServiceOption {
  id: number
  name: string
  isIntake: boolean
}

export enum EntityCodeType {
  District = 'District',
  AgreementNumber = 'AgreementNumber'
}

export enum EntityCodePrompt {
  None = 'None',
  SingleEntityCode = 'SingleEntityCode',
  TwoEntityCodes = 'TwoEntityCodes'
}

export interface ISDProgramOption {
  id: number
  name: string
  isIntake: boolean
  promptType: EntityCodePrompt
  codeType: EntityCodeType
  fiscalEntityCode: string | null
  fiscalEntityCode2: string | null
}

export interface carenetworkService {
  allowConcurrent: boolean
  customDate: Date | null
  dateRange: dateRange | null
  desiredSchedule: string
  enrollmentID: number | null
  facilityID: number
  facilityName: string | null
  inactive: boolean
  isdServiceProviderProgramID: number | null
  isdServiceProviderServiceID: number
  lastModified: Date | null
  serviceID: number
  serviceLogo?: string
  serviceName: string
  serviceProviderID: number
  serviceProviderLogo?: string | null
  serviceProviderName: string
  sessionBlockName?: string | null
  programID: number | null
  programName: string | null
  sessionName?: string
  status: string
  timeFrameID: number
  timeFrameName: string | null
  s3Key: string | null
  type: string
  requiresIncomeCalculation: boolean
  queueOrder: number
  notes: string | null
}

export interface TransferReferralRequest {
  referralId: number
  note: string
  serviceProviderId?: number | null
  transferReason: string
}

export interface session {
  id: number
  name: string
  startDate: Date | null
  endDate: Date | null
  facilityRoomID: number
  roomName?: string
  facilityName?: string
  serviceID: number
  schoolYearID: number
  sessionHours: businessHours[]
  staffLeadID: number | null
  staffLeadName: string | null
  sessionStaff: sessionStaffMember[]
  facilityID: number | null
  programs: seatAllocation[]
  currentEnrollments: number
  currentReferrals: number
  noCapacityLimit: boolean
  deliveryMethodID: number | null
  deliveryMethodName: string | null
  inactive: boolean
}

export interface sessionBlock {
  id: number
  name: string
  startDate: Date | null
  endDate: Date | null
  lastModified: Date
  sessionCount: number
  dateRange: dateRange | null
  selectedProgramIds: number[]
  inactive: boolean
}

export interface sessionStaffMember {
  id: number
  name?: string
}

export interface staffMember {
  id: number
  serviceProviderID: number
  firstName: string
  lastName: string
  inactive: boolean
}

export interface userAuthorizedFunction {
  id: number
  isdid: number | null
  serviceProviderID: number | null
  authorizedFunction: string
}

export interface waiver {
  id: number
  childID: number
  isdid: number
  childName: string
  studentImageKey?: string
  dateOfBirth: Date
  requestedProviderDetails: string
  providerImageKey?: string
  requestedFacility: string
  requestedProgramName: string | null
  isPriorityProgram: boolean
  addressLine1?: string
  addressLine2?: string
  povertyPercent?: number
  householdIncome?: number
  incomeVerified: boolean
  schoolYearStartingYear: number
  notes?: string
  eligibilityFactors: eligibilityFactor[]
  otherEligibilityFactor: string | null
  createdByName: string
  createdByEmail: string
  createdByContact: string
  status: string
}

export interface postError {
  field: string
  group?: string
  error: string
}

export interface ISDSettings {
  isdName: string
  showServicesForMyChild: boolean
  showServicesForMyself: boolean
  showCreateReferral: boolean
  adultServicesName?: string
  adultServicesLink?: string
  v4Enabled?: boolean
}
export enum ProgramVerificationStatus {
  NotVerified = 'NotVerified',
  VerificationPending = 'VerificationPending',
  VerificationPendingWithAction = 'VerificationPendingWithAction',
  Verified = 'Verified'
}

export interface NewEnrollmentDetails {
  id: number | null
  isdID: number
  programName: string
  programID: number | null
  programS3Key: string | null
  isdspProgramID: number | null
  timeSpanID: number | null
  timeSpanName: string | null
  programVerificationID?: number
  programVerificationStatus?: ProgramVerificationStatus
  serviceName: string
  serviceID: number
  isdspServiceID: number
  serviceS3Key?: string
  districtName: string
  sessionBlockName: string
  sessionID: number
  sessionName?: string
  facilityName: string | null
  desiredSchedule: string
  desiredScheduleID: number | null
  facilityID: string | null
  statusID: number
  lastActionDate: Date
  notes: string
  rank: number | null
  isdServiceProviderID: number | null
  serviceProviderID: number | null
  serviceProviderName: string
  childID: number
  declinedOrExitedReason: string | null
}
export interface ProgramVerificationDetails {
  id: number
  childID: number
  childName: string
  programName: string
  status: ProgramVerificationStatus
  annualIncome: number | null
  baseFPL: number | null
  additionalPerMember: number | null
  isdName: string | null
  prioritizedProgramName: string | null
  prioritizedProgramFPL: number | null
  verifiedFederalPovertyLevel: string | null
  prioritizedProgramCutoff: number | null
  incomeCutoff: number | null
  verifiedAnnualIncome: number | null
  reportedIncomeID: number | null
  federalPovertyLevel: string | null
  memberCount: number
  timeSpanName: string
  timeSpanYears: string[]
  expireDate: Date | null
  isValid: boolean
  notes: string | null
  incomeApprovals: ProgramVerificationIncomeApproval[]
  requiredDocType: number | null
  requiredDocTarget: DocumentTarget
  optionalDocTypes: documentTypeModel[]
  isInUse: boolean
  acknowledgeIncomeGap: boolean
  autoWaiver: boolean
}
export interface ProgramVerificationIncomeApproval {
  incomeApprovalID: number
  approved: boolean | null
  decidingUserName: string | null
  userOrganization: string | null
  whenDecided: Date | null
  notes: string | null
  reasonID: number | null
  reason: string | null
  type: 'overIncome' | 'waiver'
}

export interface EnrollmentDetails {
  unbornChild: boolean
  isCaregiverOfChild: boolean
  userServiceProviderId: number | null
  hasStartedCreatingAccount: boolean
  currentStep: number
  currentSubStep: number
  note: string
  currentChild: PortalChildDetails
  currentCaregiver: PortalCaregiverDetails
  children: PortalChildDetails[]
  caregivers: PortalCaregiverDetails[]
  isNewSession: boolean
}

export interface PortalChildDetails {
  firstName: string | null
  lastName: string
  middleName?: string
  nickname?: string
  suffix?: string
  dateOfBirth: Date | null
  birthGender?: string
  races?: number[]
  tribalAffiliationStatus?: boolean | null
  tribalAffiliationID?: number | null
  tribalAffiliationName?: number | null
  languageID: number | null
  hasIEP?: boolean
  hasIFSP?: boolean
  hasEOIS?: boolean
  fosterChild?: boolean
  hasSNAPBenefits?: boolean
  isOnCashAssistance?: boolean
  isOnSSI?: boolean
  isHomeless: boolean
  isImmigrant?: boolean
  dueDate?: Date | null
  unborn: boolean
  livesWithFirstChild?: boolean
  isCurrentlyUpdating: boolean
  location: PortalLocation
  requestedServices?: PortalService[]
}

export interface PortalCaregiverDetails {
  firstName: string
  lastName: string
  dateOfBirth?: Date | null
  relationshipId?: number | null
  hasFinancialResponsibility?: boolean
  primaryPhone: string
  secondaryPhone: string
  email: string
  doesNotHaveEmail: boolean
  preferredContactMethod: number | null
  educationLevelID?: number | null
  isLivingWithStudent: boolean
  location: PortalLocation
  isCurrentlyUpdating: boolean
  primaryLanguageId?: number | null
}

export interface PortalLocation {
  address: string
  apartment: string
  city: string
  state: string
  zipCode: string
}

export interface PortalService {
  id: number
  timeFrame: ReferralTimeFrame | null
  startDate: Date | null
  desiredSchedule: DesiredSchedule | null
  facilityIds: number[]
  reachOut: boolean
  isCurrentlyUpdating: boolean
  annualIncome: number | null
  householdMembers: number | null
}

export interface PortalServiceProviderSearchResult {
  facilityID: number
  facilityName: string
  serviceProviderName: string
  facilityHours: PortalFacilityHours[]
  address: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  latitude: number | null
  longitude: number | null
  phoneNumber: string | null
  website: string | null
  qualityRating: string
  offeringNarrative: string
  providesBeforeAfterSchoolCare: boolean
  programAges: number[]
  programs: PortalProgram[]
}

export interface PortalFacilityHours {
  dayOfWeek: string
  openTime: string
  closeTime: string
}

export interface ServiceLog {
  serviceName: string
  status: string
  s3Key: string | null
}
export interface PortalProgram {
  name: string
  description: string | null
  s3Key: string | null
}

export interface PortalFacilityViewModel {
  facilityID: number
  facilityName: string
  facilityHours: PortalFacilityHours[]
  address: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  latitude: number | null
  longitude: number | null
}

export interface InterestFormLabel {
  id: number
  labelText: string
  hoverText: string
  name: string
}

export interface PortalChildSummary {
  id: number
  firstName: string
  lastName: string
  dateOfBirth: Date | null
  unborn: boolean
}

export interface PortalCaregiverSummary {
  id: number
  firstName: string
  lastName: string
  phoneNumber: string | null
  email: string | null
}

export interface ReferralDocumentModel {
  id: number
  childID: number
  fileName: string
  whenUploaded: Date
  verified: boolean
  verifiedDate: Date | null
  verifiedBy: string | null
  verificationDetails: string | null
  documentIncome: number | null
  incomeYear: number | null
  caregivers: lookup[]
  isLinked: boolean
}

export interface DocumentVerifyModel {
  documentID: number
  documentTypeID?: number
  incomeRelated: boolean
  incomeAmount: number | null
  incomeYear: number | null
  isVerified: boolean
  verifyNotes: string | null
  verifiedBy: string | null
  verifiedOn: Date | null
  fileName: string | null
  caregivers: DocumentCaregiverModel[]
}

export interface DocumentCaregiverModel {
  caregiverID: number
  name: string
  selected: boolean
}

export interface SupplementalNutritionModel {
  id: number | null
  childID: number
  startDate: Date
  endDate: Date | null
  supplementalNutritionEligibilityID: number
  supplementalNutritionEligibility: string | null
  supplementalNutritionSourceID: number
  supplementalNutritionSource: string | null
}

export interface Restraint {
  id: number | null
  enrollmentID: number
  userID: number
  whenEntered: Date
  studentHasMedicalCondition: boolean
  medicalConditionDescription: string | null
  eventDate: Date
  whatHappenedBefore: string | null
  behaviorDescription: string | null
  behaviorDurationBeforeIntervention: string | null
  strategiesToStopBehavior: string | null
  preventionStrategiesDuration: string | null
  timeInterventionStarted: string | null
  whoInitiatedIntervention: string | null
  staffInvolved: string | null
  interventionLocation: string | null
  restraintDescription: string | null
  minutesRestrained: number
  extendedTimeExplanation: string | null
  studentResponse: string | null
  wereInjuriesSustained: boolean
  injuryDescription: string | null
  followUp: string | null
  dateParentContacted: Date | null
  timeParentContacted: Date | null
  reportGivenToParent: boolean
  writtenReportEffort: string | null
}

export interface ReportedIncome {
  annualIncome: number | null
  householdMembers: number | null
}
export interface EnrollmentData {
  id: number
  statusId: NewEnrollmentStatus
  sessionId: number | null
  sessionIsFull: boolean
  isdId: number
  isdServiceProviderProgramId: number
  programId: number
  programName: string
  programTimeSpanName: string
  programVerificationStatus: ProgramVerificationStatus
  programVerified: boolean
  validAge: boolean
  isNextInLine: boolean
  facilityName: string | null
  sessionName: string | null
  deliverySchedule: string | null
  childId: number
  childName: string
  providerName: string
  serviceId: number
  serviceName: string
  serviceS3Key: string | null
  sessionBlockName: string | null
  isMissingData: boolean
}

export interface EnrollmentUpdateInfo {
  serviceProviderName: string
  serviceName: string
  serviceS3Key: string | null
  isdSpProgramID: number
  programID: number
  programName: string
  timeSpanName: string
  sessionBlockID: number
  timeFrameID: number | null
  facilityID: number | null
  sessionID: number | null
  deliveryScheduleID: number | null
  currentStatus: NewEnrollmentStatus
  waitlistStartDate: Date | null
  enrollmentDate: Date | null
  startDate: Date | null
  verificationStatus: ProgramVerificationStatus
  facilityList: lookup[]
  deliveryScheduleList: lookup[]
  isValid: boolean
}

export interface EnrollmentUpdateResponse {
  enrollmentID: number
  facilityID: number | null
  sessionID: number | null
  deliveryScheduleID: number | null
  waitlistImmediately: boolean
  waitlistStartDate: Date | null
  enrollImmediately: boolean
  enrollmentDate: Date | null
  startDate: Date | null
}

export interface programSummary {
  id: number
  name: string
  seats: number
  filledSeats: number
}
